// Zadanie zamkniete E8: iloraz poteg zapisany jako potega liczby 2.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function shuffle(values) {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1));
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result;
}

function createOptions(correctExponent, preferred = null) {
  const exponents = preferred || shuffle([
    correctExponent,
    Math.abs(correctExponent - 1) || 1,
    correctExponent + 1,
    correctExponent * 2,
  ]);
  const unique = [];
  for (const exponent of exponents) {
    if (exponent > 0 && !unique.includes(exponent)) unique.push(exponent);
  }
  for (let exponent = 2; unique.length < 4; exponent += 1) {
    if (!unique.includes(exponent)) unique.push(exponent);
  }

  return unique.slice(0, 4).map((exponent, index) => ({
    label: LETTERS[index],
    exponent,
    latex: `2^{${exponent}}`,
  }));
}

function buildTask(baseExponentA, exponentA, baseExponentB, exponentB, options) {
  const resultExponent = baseExponentA * exponentA - baseExponentB * exponentB;
  const builtOptions = options || createOptions(resultExponent);
  return {
    baseExponentA,
    exponentA,
    baseExponentB,
    exponentB,
    baseA: 2 ** baseExponentA,
    baseB: 2 ** baseExponentB,
    resultExponent,
    options: builtOptions,
    correctIndex: builtOptions.findIndex(
      (option) => option.exponent === resultExponent
    ),
  };
}

function createOriginalTask() {
  return {
    type: "E8PowerQuotientBaseTwoClosedTask",
    data: buildTask(3, 6, 2, 3, createOptions(12, [2, 3, 4, 12])),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 8,
    },
    tags: ["e8", "potegi", "iloraz_poteg"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  for (let attempt = 0; attempt < 100; attempt += 1) {
    const baseExponentA = pick([2, 3, 4]);
    const baseExponentB = pick([2, 3, 4]);
    const exponentA = pick([3, 4, 5, 6]);
    const exponentB = pick([2, 3, 4]);
    const resultExponent =
      baseExponentA * exponentA - baseExponentB * exponentB;

    if (resultExponent >= 2 && resultExponent <= 16) {
      return {
        type: "E8PowerQuotientBaseTwoClosedTask",
        data: buildTask(
          baseExponentA,
          exponentA,
          baseExponentB,
          exponentB
        ),
        source: { variant: "random", basedOn: "E8_2025_MAJ_8" },
        tags: ["e8", "potegi", "iloraz_poteg"],
      };
    }
  }

  return {
    ...createOriginalTask(),
    source: { variant: "random", basedOn: "E8_2025_MAJ_8" },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <p>
        Wartość wyrażenia{" "}
        <InlineMath
          math={`${data.baseA}^{${data.exponentA}}:${data.baseB}^{${data.exponentB}}`}
        />{" "}
        można zapisać w postaci
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "8px",
        }}
      >
        {data.options.map((option) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={option.label} math={option.latex} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        value: index,
        label: option.label,
        textNode: option.label,
      }))}
      value={userAnswer?.choiceIndex ?? null}
      onChange={(choice) => {
        const choiceIndex =
          typeof choice === "number"
            ? choice
            : Number(choice?.target?.value ?? choice?.value);
        onChange({ choiceIndex });
      }}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const option = data.options[data.correctIndex];
  return <ChoiceOptionMath letter={option.label} math={option.latex} />;
}

export function renderUserAnswer(userAnswer, task) {
  const option = task?.data?.options?.[userAnswer?.choiceIndex];
  return option ? (
    <ChoiceOptionMath letter={option.label} math={option.latex} />
  ) : (
    <i>(brak odpowiedzi)</i>
  );
}

export function renderExplanation({ data }) {
  const correctOption = data.options[data.correctIndex];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Zapisujemy obie podstawy jako potęgi liczby{" "}
        <InlineMath math="2" />.
      </p>
      <BlockMath
        math={`${data.baseA}=2^{${data.baseExponentA}},\\qquad ${data.baseB}=2^{${data.baseExponentB}}`}
      />
      <p>
        <strong>Krok 2.</strong> Korzystamy ze wzoru na potęgę potęgi:{" "}
        <InlineMath math={"(a^m)^n=a^{m \\cdot n}"} />.
      </p>
      <BlockMath
        math={`${data.baseA}^{${data.exponentA}}=\\left(2^{${data.baseExponentA}}\\right)^{${data.exponentA}}=2^{${data.baseExponentA}\\cdot${data.exponentA}}=2^{${data.baseExponentA * data.exponentA}}`}
      />
      <BlockMath
        math={`${data.baseB}^{${data.exponentB}}=\\left(2^{${data.baseExponentB}}\\right)^{${data.exponentB}}=2^{${data.baseExponentB}\\cdot${data.exponentB}}=2^{${data.baseExponentB * data.exponentB}}`}
      />
      <p>
        <strong>Krok 3.</strong> Przy dzieleniu potęg o tej samej podstawie
        odejmujemy wykładniki.
      </p>
      <BlockMath
        math={`2^{${data.baseExponentA * data.exponentA}}:2^{${data.baseExponentB * data.exponentB}}=2^{${data.baseExponentA * data.exponentA}-${data.baseExponentB * data.exponentB}}=2^{${data.resultExponent}}`}
      />
      <p>
        <strong>Krok 4.</strong> Poprawna jest odpowiedź{" "}
        <ChoiceOptionMath letter={correctOption.label} math={correctOption.latex} />
        .
      </p>
    </div>
  );
}
